/** Screen-space geometry shared by ruler painting and guide hit testing. */
export const RULER_BAND = 20;
/**
 * `top`/`left`/`right`/`bottom` is the page's room — what Fit centres inside. `rulers`, when
 * given, is where the bands themselves go: the chrome's real edges (the header's bottom,
 * the Animate bar's top), which the room keeps clear of as well, plus the context band.
 */
export interface RulerInset { top: number; left: number; right?: number; bottom?: number; rulers?: { top: number; bottom: number } }
export interface RulerRect { x: number; y: number; width: number; height: number }
/**
 * Two flat bands hugging the working area's top and left edges, meeting in a square corner
 * that names the unit — the ruler every drawing app draws, not a floating pill. The working
 * area is what the chrome leaves (`inset`): the bands run from the rail to the trailing
 * panel and from the header to the window's bottom (the dock and the zoom pill float over
 * the canvas; the zoom pill steps right of the band), so nothing of the chrome is ruled over.
 */
export function rulerLayout(width: number, height: number, inset: RulerInset) {
  const x = inset.left;
  const y = inset.rulers?.top ?? inset.top;
  const right = width - (inset.right ?? 0);
  const bottom = height - (inset.rulers?.bottom ?? inset.bottom ?? 0);
  const corner = right >= x + RULER_BAND && bottom >= y + RULER_BAND
    ? { x, y, width: RULER_BAND, height: RULER_BAND } : null;
  return {
    corner,
    horizontal: corner && right - x - RULER_BAND >= 56 ? { x: x + RULER_BAND, y, width: right - x - RULER_BAND, height: RULER_BAND } : null,
    vertical: corner && bottom - y - RULER_BAND >= 56 ? { x, y: y + RULER_BAND, width: RULER_BAND, height: bottom - y - RULER_BAND } : null,
  };
}
export type RulerLayout = ReturnType<typeof rulerLayout>;
function contains(rect: RulerRect | null, x: number, y: number) {
  return !!rect && x >= rect.x && y >= rect.y && x < rect.x + rect.width && y < rect.y + rect.height;
}
export function bandAt(x: number, y: number, layout: RulerLayout): 'x' | 'y' | 'corner' | null {
  if (contains(layout.corner, x, y)) return 'corner';
  if (contains(layout.vertical, x, y)) return 'x';
  if (contains(layout.horizontal, x, y)) return 'y';
  return null;
}
/** Returning a guide to either band (or beyond its near edge) removes it. */
export function backInRulers(x: number, y: number, layout: RulerLayout) {
  return bandAt(x, y, layout) !== null || !!layout.corner &&
    (x < layout.corner.x + RULER_BAND || y < layout.corner.y + RULER_BAND);
}
